import type { CSSProperties, FC, ReactNode } from 'react'
import type { ContactTopic } from '@/constants/contact'

import Image from 'next/image'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Blob } from '@/components/generals/blob'
import { buttonClasses } from '@/components/generals/button'
import { Scallop } from '@/components/generals/scallop'
import { Sticker } from '@/components/generals/sticker'
import { Tilt } from '@/components/generals/tilt'
import { TEAM } from '@/constants/home'

interface TeamProps {
    onApply: (topic: ContactTopic) => void
}

const HIGHLIGHT = 'inline-block cursor-default transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-rotate-2 hover:scale-105'

export const Team: FC<TeamProps> = ({ onApply }): ReactNode => {
    return (
        <section id='team' aria-labelledby='team-title' className='on-dark relative bg-plum-deep py-24 text-cream sm:py-32'>
            <Scallop className='text-plum-deep' />

            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <div className='relative max-w-5xl'>
                    <Sticker
                        label='Who we are'
                        shape='flower'
                        colorClassName='text-mint'
                        className='reveal-pop -mb-2 size-24 -rotate-12 text-base motion-safe:animate-float sm:size-28 sm:text-lg'
                    />

                    <h2 id='team-title' className='sr-only'>
                        Meet the team
                    </h2>

                    <p className='reveal mt-6 font-heading text-[clamp(1.9rem,4.2vw,3.6rem)] leading-[1.08]'>
                        Huzzah started in 2016 around a borrowed kitchen table, too much coffee and one stubborn belief:{' '}
                        <span className={cn(HIGHLIGHT, 'text-butter')}>small brands deserve big ideas too.</span>{' '}
                        Today we're a tight crew of designers, writers and developers who still get{' '}
                        <span className={cn(HIGHLIGHT, 'text-bubblegum hover:rotate-2')}>way too excited</span>{' '}
                        about a good launch.
                    </p>
                </div>

                <ul className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                    {TEAM.map((member, index) => (
                        <li
                            key={member.name}
                            className='reveal'
                            style={{ '--reveal-delay': `${index * 120}ms` } as CSSProperties}
                        >
                            <Tilt max={10} className='h-full'>
                                <article className={cn('group relative h-full rounded-[2rem] p-3 text-plum transition-shadow duration-300 hover:shadow-[0_10px_0_0_var(--color-butter)]', member.color)}>
                                    <div className='relative aspect-[4/5] overflow-hidden rounded-[1.5rem] border-2 border-plum bg-cream'>
                                        <Image
                                            src={member.image}
                                            alt={member.imageAlt}
                                            fill
                                            sizes='(min-width: 1024px) 270px, (min-width: 640px) 45vw, 92vw'
                                            className={cn('object-cover transition-transform duration-700 ease-out group-hover:scale-110', member.imagePosition)}
                                        />
                                    </div>

                                    <Sticker
                                        label={member.nickname}
                                        shape={member.stickerShape}
                                        colorClassName={member.stickerColor}
                                        className='absolute -top-4 -right-3 size-24 rotate-12 text-base transition-[scale,rotate] duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-125 group-hover:-rotate-12 motion-safe:animate-wiggle'
                                    />

                                    <div className='px-3 pt-5 pb-3'>
                                        <h3 className='font-heading text-[1.75rem] leading-none transition-transform duration-300 group-hover:translate-x-1'>
                                            {member.name}
                                        </h3>

                                        <p className='mt-1.5 font-medium text-plum/75'>
                                            {member.role}
                                        </p>
                                    </div>
                                </article>
                            </Tilt>
                        </li>
                    ))}

                    <li className='reveal [--reveal-delay:360ms]'>
                        <article className='group flex h-full min-h-[26rem] flex-col items-center justify-center rounded-[2rem] border-2 border-dashed border-cream/40 p-8 text-center transition-colors duration-300 hover:border-butter hover:bg-cream/5'>
                            <div aria-hidden='true' className='relative grid size-32 place-items-center transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-110'>
                                <Blob shape='burst' className='absolute inset-0 size-full text-coral motion-safe:animate-spin-slow' />
                                <Plus className='relative size-12 text-cream transition-transform duration-500 group-hover:rotate-180' strokeWidth={2.5} />
                            </div>

                            <h3 className='mt-6 font-heading text-[2rem] leading-none'>
                                Could be you
                            </h3>

                            <p className='mt-2 font-medium text-cream/75'>
                                We're hiring a Senior Motion Designer
                            </p>

                            <a
                                href='#contact'
                                onClick={() => onApply('Joining the team')}
                                className={buttonClasses('dark', 'sm', 'mt-6')}
                            >
                                Get in touch
                            </a>
                        </article>
                    </li>
                </ul>
            </div>
        </section>
    )
}
